import { useState, useEffect, useRef, useCallback } from 'react';
import { authAPI } from '../services/api';

interface DispatcherLocationState {
  location: { lat: number; lng: number } | null;
  isAvailable: boolean;
  isTracking: boolean;
  lastUpdate: Date | null;
  error: string | null;
  permission: 'granted' | 'denied' | 'prompt';
}

interface QueuedUpdate {
  latitude: number;
  longitude: number;
  is_available: boolean;
  timestamp: number;
}

const LOCATION_STORAGE_KEY = 'dispatcher_last_location';
const QUEUE_STORAGE_KEY = 'dispatcher_location_queue';
const UPDATE_THROTTLE_MS = 7000; // 7 seconds

export const useDispatcherLocation = () => {
  const [state, setState] = useState<DispatcherLocationState>({
    location: null,
    isAvailable: true,
    isTracking: false,
    lastUpdate: null,
    error: null,
    permission: 'prompt',
  });

  const watchIdRef = useRef<number | null>(null);
  const lastUpdateTimeRef = useRef<number>(0);
  const updateQueueRef = useRef<QueuedUpdate[]>([]);
  const isUpdatingRef = useRef<boolean>(false);

  // Load persisted state from localStorage
  useEffect(() => {
    try {
      const savedLocation = localStorage.getItem(LOCATION_STORAGE_KEY);
      if (savedLocation) {
        const { lat, lng, timestamp } = JSON.parse(savedLocation);
        setState(prev => ({
          ...prev,
          location: { lat, lng },
          lastUpdate: new Date(timestamp),
        }));
      }

      const savedQueue = localStorage.getItem(QUEUE_STORAGE_KEY);
      if (savedQueue) {
        updateQueueRef.current = JSON.parse(savedQueue);
      }
    } catch (error) {
      console.error('Error loading persisted location:', error);
    }
  }, []);

  // Check geolocation permission
  useEffect(() => {
    if (!navigator.geolocation) {
      setState(prev => ({ ...prev, error: 'Geolocation not supported' }));
      return;
    }

    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setState(prev => ({ ...prev, permission: result.state as 'granted' | 'denied' | 'prompt' }));
        
        result.addEventListener('change', () => {
          setState(prev => ({ ...prev, permission: result.state as 'granted' | 'denied' | 'prompt' }));
        });
      });
    }
  }, []);

  // Persist location to localStorage
  const persistLocation = (lat: number, lng: number) => {
    try {
      localStorage.setItem(
        LOCATION_STORAGE_KEY,
        JSON.stringify({ lat, lng, timestamp: Date.now() })
      );
    } catch (error) {
      console.error('Error persisting location:', error);
    }
  };

  // Queue update for background sync
  const queueUpdate = (update: QueuedUpdate) => {
    updateQueueRef.current.push(update);
    try {
      localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(updateQueueRef.current));
    } catch (error) {
      console.error('Error queuing update:', error);
    }
  };

  // Send location update to backend
  const sendLocationUpdate = async (latitude: number, longitude: number, isAvailable: boolean, queuedTimestamp?: number) => {
    if (isUpdatingRef.current) return;
    
    try {
      isUpdatingRef.current = true;
      
      await authAPI.updateRiderLocation({
        latitude,
        longitude,
        is_available: isAvailable,
      });

      setState(prev => ({
        ...prev,
        lastUpdate: new Date(),
        error: null,
      }));

      // Remove from queue if this was a queued update
      if (queuedTimestamp) {
        updateQueueRef.current = updateQueueRef.current.filter(
          item => item.timestamp !== queuedTimestamp
        );
        localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(updateQueueRef.current));
      }

      console.log('📍 Location updated successfully:', { latitude, longitude });
    } catch (error: any) {
      console.error('⚠️ Error updating location:', error);
      
      // Queue for retry if not already queued
      if (!queuedTimestamp) {
        queueUpdate({
          latitude,
          longitude,
          is_available: isAvailable,
          timestamp: Date.now(),
        });
      }

      setState(prev => ({
        ...prev,
        error: 'Failed to update location - will retry',
      }));
    } finally {
      isUpdatingRef.current = false;
    }
  };

  // Process queued updates
  const processQueue = useCallback(async () => {
    if (updateQueueRef.current.length === 0 || isUpdatingRef.current) return;

    const queue = [...updateQueueRef.current];
    for (const update of queue) {
      await sendLocationUpdate(update.latitude, update.longitude, update.is_available, update.timestamp);
    }
  }, []);

  // Handle position update from watchPosition
  const handlePositionUpdate = useCallback((position: GeolocationPosition) => {
    const { latitude, longitude } = position.coords;
    const now = Date.now();

    // Update state immediately
    setState(prev => ({
      ...prev,
      location: { lat: latitude, lng: longitude },
      error: null,
    }));

    persistLocation(latitude, longitude);

    // Throttle backend updates
    if (now - lastUpdateTimeRef.current >= UPDATE_THROTTLE_MS) {
      lastUpdateTimeRef.current = now;
      sendLocationUpdate(latitude, longitude, state.isAvailable);
    }
  }, [state.isAvailable]);

  // Handle geolocation errors
  const handlePositionError = useCallback((error: GeolocationPositionError) => {
    let errorMessage = 'Failed to get location';
    
    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = 'Location permission denied. Please enable location access.';
        setState(prev => ({ ...prev, permission: 'denied' }));
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage = 'Location information unavailable';
        break;
      case error.TIMEOUT:
        errorMessage = 'Location request timed out';
        break;
    }

    setState(prev => ({ ...prev, error: errorMessage }));
    console.error('📍 Geolocation error:', errorMessage);
  }, []);

  // Start continuous tracking
  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setState(prev => ({ ...prev, error: 'Geolocation not supported' }));
      return;
    }

    if (watchIdRef.current !== null) {
      console.log('⚠️ Tracking already active');
      return;
    }

    console.log('🚀 Starting continuous location tracking');

    watchIdRef.current = navigator.geolocation.watchPosition(
      handlePositionUpdate,
      handlePositionError,
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5000,
      }
    );

    setState(prev => ({ ...prev, isTracking: true, error: null }));

    // Process any queued updates
    processQueue();
  }, [handlePositionUpdate, handlePositionError, processQueue]);

  // Stop tracking
  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      console.log('⏹️ Stopped location tracking');
    }

    setState(prev => ({ ...prev, isTracking: false }));
  }, []);

  // Toggle availability
  const toggleAvailability = useCallback(async () => {
    const newAvailability = !state.isAvailable;
    
    setState(prev => ({ ...prev, isAvailable: newAvailability }));

    if (state.location) {
      await sendLocationUpdate(
        state.location.lat,
        state.location.lng,
        newAvailability
      );
    }
  }, [state.isAvailable, state.location]);

  // Auto-start tracking if permission is granted
  useEffect(() => {
    if (state.permission === 'granted' && !state.isTracking) {
      startTracking();
    }

    return () => {
      stopTracking();
    };
  }, [state.permission]);

  // Process queue periodically
  useEffect(() => {
    const interval = setInterval(processQueue, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [processQueue]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('📱 Page hidden - location tracking continues in background');
      } else {
        console.log('📱 Page visible - processing queued updates');
        processQueue();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [processQueue]);

  return {
    ...state,
    startTracking,
    stopTracking,
    toggleAvailability,
    processQueue,
  };
};
